<template>
  <div class="flex flex-col items-center">
    <button
      :class="buttonClasses"
      :disabled="disabled"
      @click="$emit('click')"
      @mousedown="startPress"
      @mouseup="endPress"
      @mouseleave="endPress"
    >
      <div class="flex justify-center items-center">
        <!-- Loading spinner when processing -->
        <div v-if="isProcessing" class="mr-2 w-8 h-8 animate-spin">
          <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <!-- Microphone icon when not processing -->
        <svg 
          v-else
          class="mr-2 w-8 h-8" 
          fill="currentColor" 
          viewBox="0 0 24 24"
        >
          <path d="M12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14S15 12.66 15 11V5C15 3.34 13.66 2 12 2M17 11C17 14.53 14.39 17.44 11 17.93V21H13C13.55 21 14 21.45 14 22S13.55 23 13 23H11C10.45 23 10 22.55 10 22S10.45 21 11 21H11V17.93C7.61 17.44 5 14.53 5 11H7C7 13.76 9.24 16 12 16S17 13.76 17 11H17Z"/>
        </svg>
        <span class="font-bold text-lg">
          {{ buttonText }}
        </span>
      </div>
    </button>
    
    <!-- Recording status indicator -->
    <div v-if="isRecording" class="flex justify-center items-center mt-4">
      <div class="bg-red-500 mr-2 rounded-full w-4 h-4 animate-pulse"></div>
      <span class="font-bold text-red-600 text-sm uppercase tracking-wide">
        Recording...
      </span>
    </div>
    
    <!-- Processing status indicator -->
    <div v-else-if="isProcessing" class="flex justify-center items-center mt-4">
      <div class="bg-blue-500 mr-2 rounded-full w-4 h-4 animate-bounce"></div>
      <span class="font-bold text-blue-600 text-sm uppercase tracking-wide">
        Processing Voice...
      </span>
    </div>
    
    <!-- Status message -->
    <div v-if="statusMessage" class="mt-2 max-w-md text-center">
      <p class="font-medium text-gray-700 text-sm">
        {{ statusMessage }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

interface Props {
  isRecording?: boolean
  isProcessing?: boolean
  disabled?: boolean
  statusMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  isRecording: false,
  isProcessing: false,
  disabled: false,
  statusMessage: ''
})

defineEmits<{
  click: []
}>()

const isPressed = ref(false)

const buttonText = computed(() => {
  if (props.isProcessing) return 'Processing...'
  if (props.isRecording) return 'Stop Recording'
  return 'Start Recording'
})

const buttonClasses = computed(() => {
  const baseClasses = [
    'px-8',
    'py-6',
    'font-bold',
    'border-4',
    'border-black',
    'transition-all',
    'duration-150',
    'transform',
    'uppercase',
    'tracking-wider',
    'text-lg',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:active:translate-x-0',
    'disabled:active:translate-y-0'
  ]
  
  if (props.disabled || props.isProcessing) {
    return [...baseClasses, 'bg-gray-300', 'shadow-[5px_5px_0px_0px_rgba(0,0,0,1)]']
  }
  
  if (props.isRecording) {
    return [
      ...baseClasses,
      'bg-red-400',
      'hover:bg-red-500',
      'animate-pulse',
      'shadow-[5px_5px_0px_0px_rgba(0,0,0,1)]',
      'active:translate-x-1',
      'active:translate-y-1',
      'active:shadow-none'
    ]
  }
  
  if (isPressed.value) {
    return [
      ...baseClasses,
      'bg-green-500',
      'translate-x-1',
      'translate-y-1',
      'shadow-none'
    ]
  }
  
  return [
    ...baseClasses,
    'bg-green-400',
    'hover:bg-green-500',
    'shadow-[5px_5px_0px_0px_rgba(0,0,0,1)]',
    'active:translate-x-1',
    'active:translate-y-1',
    'active:shadow-none'
  ]
})

const startPress = () => {
  if (!props.disabled && !props.isProcessing) {
    isPressed.value = true
  }
}

const endPress = () => {
  isPressed.value = false
}
</script> 